import L from "leaflet";

const cornerOk = c =>
  c && Number.isFinite(c.lat) && Number.isFinite(c.lng);
const boundsOk = b => b && cornerOk(b._southWest) && cornerOk(b._northEast);

// markercluster stores +-Infinity as a real coordinate: _checkBoundsMaxLat and
// _mapBoundsInfinite both use it to mean "unbounded". Comparing against that is
// exact, so only a missing or NaN corner makes a box uncomparable.
const numberOk = v => typeof v === "number" && !Number.isNaN(v);
const comparableCorner = c => c && numberOk(c.lat) && numberOk(c.lng);
const comparable = b =>
  b && comparableCorner(b._southWest) && comparableCorner(b._northEast);

// _resetBounds parks south-west above north-east until the first extend(). That
// is an absence, not an empty area, so it must not answer "no overlap".
const collected = b =>
  b._southWest.lat <= b._northEast.lat && b._southWest.lng <= b._northEast.lng;

const proto = L.LatLngBounds.prototype;

if (!proto._rrGuarded) {
  proto._rrGuarded = true;

  const orig = {
    intersects: proto.intersects,
    overlaps: proto.overlaps,
    contains: proto.contains,
    pad: proto.pad,
    isValid: proto.isValid,
    getCenter: proto.getCenter
  };

  const asBounds = bounds =>
    bounds instanceof L.LatLngBounds ? bounds : L.latLngBounds(bounds);

  proto.isValid = function() {
    return !!(orig.isValid.call(this) && boundsOk(this));
  };

  proto.getCenter = function() {
    if (!boundsOk(this)) return this._southWest || L.latLng(0, 0);
    try {
      return orig.getCenter.call(this);
    } catch (e) {
      return this._southWest || L.latLng(0, 0);
    }
  };

  proto.intersects = function(bounds) {
    if (!comparable(this) || !collected(this)) return true;
    const other = asBounds(bounds);
    if (!comparable(other) || !collected(other)) return true;
    try {
      return orig.intersects.call(this, other);
    } catch (e) {
      return true;
    }
  };

  proto.overlaps = function(bounds) {
    if (!comparable(this) || !collected(this)) return true;
    const other = asBounds(bounds);
    if (!comparable(other) || !collected(other)) return true;
    try {
      return orig.overlaps.call(this, other);
    } catch (e) {
      return true;
    }
  };

  proto.contains = function(obj) {
    if (!comparable(this)) return false;
    try {
      return orig.contains.call(this, obj);
    } catch (e) {
      return false;
    }
  };

  proto.pad = function(bufferRatio) {
    if (!boundsOk(this)) return this;
    try {
      return orig.pad.call(this, bufferRatio);
    } catch (e) {
      return this;
    }
  };
}

const mapProto = L.Map.prototype;
if (!mapProto._rrBoundsZoom) {
  mapProto._rrBoundsZoom = true;
  const getBoundsZoom = mapProto.getBoundsZoom;
  mapProto.getBoundsZoom = function(bounds, inside, padding) {
    const b =
      bounds instanceof L.LatLngBounds ? bounds : L.latLngBounds(bounds);
    if (!boundsOk(b)) return this.getZoom() || 0;
    return getBoundsZoom.call(this, bounds, inside, padding);
  };
}

if (!mapProto._rrSafeView) {
  mapProto._rrSafeView = true;
  const origSetView = mapProto.setView;
  mapProto.setView = function(center, zoom, options) {
    try {
      const c = L.latLng(center);
      if (!cornerOk(c)) return this;
      return origSetView.call(this, c, zoom, options);
    } catch (e) {
      return this;
    }
  };

  const origFitBounds = mapProto.fitBounds;
  mapProto.fitBounds = function(bounds, options) {
    try {
      const b =
        bounds instanceof L.LatLngBounds ? bounds : L.latLngBounds(bounds);
      if (!boundsOk(b)) return this;
      return origFitBounds.call(this, b, options);
    } catch (e) {
      return this;
    }
  };

  const origPanTo = mapProto.panTo;
  mapProto.panTo = function(center, options) {
    try {
      const c = L.latLng(center);
      if (!cornerOk(c)) return this;
      return origPanTo.call(this, c, options);
    } catch (e) {
      return this;
    }
  };
}

const pathProto = L.Path.prototype;
if (!pathProto._rrRemovePath) {
  pathProto._rrRemovePath = true;
  const onRemove = pathProto.onRemove;
  pathProto.onRemove = function() {
    if (!this._renderer || typeof this._renderer._removePath !== "function") {
      return this;
    }
    return onRemove.apply(this, arguments);
  };

  const onAdd = pathProto.onAdd;
  pathProto.onAdd = function() {
    if (!this._renderer && this._map) {
      try {
        this._renderer = this._map.getRenderer(this);
      } catch (e) {
        return this;
      }
    }
    if (!this._renderer || typeof this._renderer._initPath !== "function") {
      return this;
    }
    return onAdd.apply(this, arguments);
  };

  const redraw = pathProto.redraw;
  pathProto.redraw = function() {
    if (!this._renderer || typeof this._renderer._updatePath !== "function") {
      return this;
    }
    return redraw.apply(this, arguments);
  };

  const clickTolerance = pathProto._clickTolerance;
  pathProto._clickTolerance = function() {
    if (!this._renderer) return 0;
    return clickTolerance.apply(this, arguments);
  };

  const reset = pathProto._reset;
  pathProto._reset = function() {
    if (!this._renderer) return;
    return reset.apply(this, arguments);
  };
}

const markerProto = L.Marker.prototype;
if (!markerProto._rrRemoveIcon) {
  markerProto._rrRemoveIcon = true;
  const removeIcon = markerProto._removeIcon;
  markerProto._removeIcon = function() {
    if (!this._icon) return;
    try {
      removeIcon.apply(this, arguments);
    } catch (e) {
      if (this._icon && this._icon.parentNode) {
        this._icon.parentNode.removeChild(this._icon);
      }
      this._icon = null;
    }
  };
}

if (!markerProto._rrRemoveShadow) {
  markerProto._rrRemoveShadow = true;
  const removeShadow = markerProto._removeShadow;
  markerProto._removeShadow = function() {
    try {
      return removeShadow.apply(this, arguments);
    } catch (e) {
      if (this._shadow && this._shadow.parentNode) {
        this._shadow.parentNode.removeChild(this._shadow);
      }
      this._shadow = null;
    }
  };
}
